import { useTranslate } from "@tolgee/react";
import React from "react";
import { useChangeLogLabels } from "../../features/change-log/changeLogLabels.js";
import type { ChangeLogEntityStrategy } from "../../features/change-log/changeLogTypes.js";

/**
 * Entity-render strategy for the project change log (design D7). The project log is a
 * union over the project aggregate and its document, order and invoice aggregates, so
 * entities are heterogeneous — the root `entityType`, not "is it a root?", picks the
 * deep link (RF-279 design D6):
 * - `DOCUMENT` → the read-time-resolved name, deep-linked to the quote. The quote
 *   aggregate is the only `DOCUMENT` root captured today; other document types arrive
 *   with their capture follow-ups.
 * - `INVOICE` → deep-linked to the project's outgoing-invoice view. A hard-deleted
 *   draft has no live row, so a deleted invoice renders label-only like everything else.
 * - `ORDER` → label-only: no order detail route exists, so an order must never inherit
 *   the (broken) quote link.
 * - `REQUIREMENT` → deep-linked to the project's requirement page. A requirement root is
 *   NOT the quote document (RF-262 design D1): the read model gives it its own type
 *   instead of `DOCUMENT`, so it must never inherit the quote link either — that would
 *   open a broken empty quote shell keyed by the requirement's id.
 * - `PROJECT` (the project row itself) → label-only: it IS this log's own scope, so
 *   there is nothing to link to. It must never inherit the quote link either — which is
 *   why the read model gives the project root its own type instead of `DOCUMENT`.
 * - child entities, deleted, and unmapped entities → label-only.
 *
 * The project children `CONTACT`, `WORKSESSION` and `SUBCONTRACT` (RF-279) are label-only
 * BY DECISION, not by omission — they fall through the `default` branch and are listed
 * nowhere because an explicit no-op `case` would only restate it:
 * - `CONTACT` / `WORKSESSION`: neither has a routable detail view at all. A contact link is
 *   edited inline in the project's contacts box and a work session in the timesheet form,
 *   both of which live on pages this log cannot address by entity id.
 * - `SUBCONTRACT`: deep-linking back to the subcontracted origin item IS possible — the
 *   capture allowlist snapshots `originQuoteId` + `originItemId` precisely to keep that
 *   option open — but it is an explicit v1 non-goal (design Open Questions), so no link is
 *   built from those values yet.
 *
 * The label is the shared kind-qualified display rule (target-naming D2) — delegated
 * wholesale to `entityDisplayLabel`, so every project-log entity reads `Kind: Title` (or
 * the kind label alone on a resolution miss) with no per-kind exemption of its own; the
 * project log has none of the quote log's home-kind formats to preserve. `aggregateLabel`
 * is the coarse "Project" fallback for a multi-entity action with no root entry to show.
 */
export function useProjectEntityStrategy(
  projectId: string
): ChangeLogEntityStrategy {
  const { t } = useTranslate(["ProjectOverview"]);
  const { entityDisplayLabel } = useChangeLogLabels();
  return React.useMemo(
    () => ({
      entityLabel: (entity, nameOverride) =>
        entityDisplayLabel(entity, nameOverride),
      entityLink: entity => {
        if (entity.isDeleted) return undefined;
        switch (entity.entityType) {
          case "DOCUMENT":
            return `/projects/${projectId}/quotes/${entity.entityId}`;
          case "INVOICE":
            return `/projects/${projectId}/invoices/outgoing/${entity.entityId}`;
          case "REQUIREMENT":
            return `/projects/${projectId}/requirements/${entity.entityId}`;
          default:
            return undefined;
        }
      },
      aggregateLabel: () => t("Project", { ns: "ProjectOverview" }),
    }),
    [t, projectId, entityDisplayLabel]
  );
}
